import { useState } from 'react';
import { api, useApi } from '../api';
import { Card } from '../components/ui';
import { useT } from '../i18n';

/**
 * Laboratorio: una sonda contra los proveedores, para medir lo que no se configura.
 *
 * ── Por qué está separado del panel ─────────────────────────────────────────────
 * Esta ruta llama a proveedores que cobran **sin pasar por el `QuotaGuard` ni por los topes
 * diario y mensual**: son mediciones, no producción. En el panel de todos los días sería un
 * botón que gasta dinero sin ningún freno, al lado de los que sí lo tienen.
 *
 * El servidor lo apaga con la misma condición: fuera de desarrollo, `/api/lab/*` devuelve 404.
 * Esta pantalla no es la defensa, es la coherencia — ofrecer una pestaña cuyas rutas responden
 * 404 sería peor que no tenerla.
 *
 * ── Por qué queda una sonda y antes había cuatro ────────────────────────────────
 * Porque las otras tres medían fal y Gemini, que salieron del sistema cuando todo lo que cobra
 * pasó a Runware. Sus rutas se borraron con ellos y la pantalla seguía llamándolas: cuatro
 * botones que respondían 404, que es exactamente lo que el comentario de arriba dice que no hay
 * que ofrecer.
 *
 * Lo que aprendieron sigue escrito donde importa —en el README y en los adaptadores— porque el
 * hallazgo sobrevive al proveedor que lo produjo.
 */

export function Laboratorio() {
  const { t } = useT();
  const [corriendo, setCorriendo] = useState(false);
  const [creador, setCreador] = useState('');
  const [lugar, setLugar] = useState('agua');
  const [extra, setExtra] = useState('');
  const [elegidos, setElegidos] = useState<string[]>([]);
  const [resultado, setResultado] = useState<{
    prompt: string;
    resultados: {
      model: string;
      ok: boolean;
      ms: number;
      medidas: string | null;
      usd: number;
      url: string | null;
      error: string | null;
    }[];
  } | null>(null);

  /**
   * Las influencers entre las que se elige, con su nivel de piel.
   *
   * Sale de la ruta de tomas y no de la lista de influencers porque hace falta una que tenga
   * material aprobado: la comparación adjunta la hoja de identidad, y sin hoja mediría qué cara
   * inventa cada modelo en vez de qué hace cada uno con la misma persona.
   */
  const tomas = useApi<{
    tomas: {
      assetId: string;
      creatorId: string;
      creator: string;
      nivelDePiel: string;
      medidas: string;
      url: string;
    }[];
  }>('/api/lab/video-sources');

  /**
   * Los modelos que se ofrecen, servidos y no escritos acá.
   *
   * La lista anterior era literal —cinco identificadores de fal escritos a mano— y sobrevivió a
   * los proveedores que nombraba: seguía ofreciendo Seedream y FLUX meses después de que se
   * fueran. Con el catálogo del servidor, la lista que se muestra y la que se puede generar son
   * la misma por construcción.
   */
  const catalogo = useApi<{
    catalogoDeModelos?: { model: string; etiqueta: string; disponible: boolean }[];
  }>('/api/settings');
  const modelos = (catalogo.data?.catalogoDeModelos ?? []).filter((m) => m.disponible);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight">{t('lab.titulo')}</h1>
        <p className="mt-1 text-sm text-[var(--color-ink-muted)]">
          {t('lab.bajada', { gastan: t('lab.gastan') })}
        </p>
      </div>

      {/*
        La misma escena por varios modelos, para poder compararlos.

        Los ajustes del prompt se agotaron: la prenda ya se nombra con términos de catálogo, va
        segunda en el orden y no hay filtro ajeno que aflojar. Lo que queda es el sesgo de
        entrenamiento de cada modelo, que no se configura — se mide.
      */}
      <Card title={t('lab.comparar')}>
        <p className="max-w-2xl text-sm text-[var(--color-ink-muted)]">
          {t('lab.mandaAntes')}
          <strong>{t('lab.mandaFuerte')}</strong>
          {t('lab.mandaDespues')}
        </p>
        <p className="mt-2 max-w-2xl text-xs text-[var(--color-ink-muted)]">
          {t('lab.cuestaAntes')}
          <strong>{t('lab.noEnGaleria')}</strong>
          {t('lab.cuestaDespues')}
        </p>

        <div className="mt-4 flex flex-wrap items-end gap-3 text-xs">
          <label>
            <span className="mb-1 block text-[var(--color-ink-muted)]">{t('lab.influencer')}</span>
            <select
              value={creador}
              onChange={(event) => setCreador(event.target.value)}
              className="w-64 rounded-xl border border-[var(--color-border-subtle)] bg-transparent px-3 py-2"
            >
              <option value="">{t('lab.elegiUna')}</option>
              {(tomas.data?.tomas ?? [])
                .filter((t, i, arr) => arr.findIndex((o) => o.creator === t.creator) === i)
                .map((t) => (
                  <option key={t.assetId} value={t.assetId}>
                    {t.creator} · {t.nivelDePiel}
                  </option>
                ))}
            </select>
          </label>

          <label>
            <span className="mb-1 block text-[var(--color-ink-muted)]">Lugar</span>
            <select
              value={lugar}
              onChange={(event) => setLugar(event.target.value)}
              className="rounded-xl border border-[var(--color-border-subtle)] bg-transparent px-3 py-2"
            >
              <option value="agua">agua · pileta o playa</option>
              <option value="gimnasio">gimnasio</option>
              <option value="interior">interior</option>
            </select>
          </label>
        </div>

        <div className="mt-3">
          <p className="mb-1 text-xs text-[var(--color-ink-muted)]">Modelos a comparar</p>
          <div className="flex flex-wrap gap-2">
            {modelos.map((modelo) => (
              <button
                key={modelo.model}
                type="button"
                onClick={() =>
                  setElegidos((actual) =>
                    actual.includes(modelo.model)
                      ? actual.filter((m) => m !== modelo.model)
                      : [...actual, modelo.model],
                  )
                }
                className={
                  'rounded-xl border px-3 py-2 text-xs transition ' +
                  (elegidos.includes(modelo.model)
                    ? 'border-[var(--color-accent)] bg-[var(--color-accent-soft)] font-medium'
                    : 'border-[var(--color-border-subtle)] hover:border-[var(--color-border-strong)]')
                }
              >
                {modelo.etiqueta}
              </button>
            ))}
          </div>

          {/* Campo libre: Runware aloja miles de modelos y el catálogo sólo nombra los probados. */}
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
            <input
              value={extra}
              onChange={(event) => setExtra(event.target.value)}
              placeholder={t('lab.pegarOtro')}
              className="min-w-0 flex-1 rounded-xl border border-[var(--color-border-subtle)] px-3 py-2 font-mono"
            />
            <button
              type="button"
              disabled={extra.trim().length < 4}
              onClick={() => {
                setElegidos((actual) => [...new Set([...actual, extra.trim()])]);
                setExtra('');
              }}
              className="rounded-xl border border-[var(--color-accent)] px-3 py-2 font-medium text-[var(--color-accent)] disabled:opacity-40"
            >
              {t('lab.agregar')}
            </button>
          </div>
        </div>

        <p className="mt-3 text-sm">
          Costo de esta comparación:{' '}
          <strong className="tabular-nums">~USD {(elegidos.length * 0.05).toFixed(2)}</strong>{' '}
          <span className="text-[var(--color-ink-muted)]">
            ({elegidos.length} {elegidos.length === 1 ? 'modelo' : 'modelos'})
          </span>
        </p>

        <div className="mt-3">
          <button
            type="button"
            disabled={corriendo || !creador || elegidos.length === 0}
            onClick={() => {
              setCorriendo(true);
              setResultado(null);
              const creatorId = (tomas.data?.tomas ?? []).find(
                (t) => t.assetId === creador,
              )?.creatorId;

              void api
                .post<typeof resultado>('/api/lab/comparar-modelos', {
                  creatorId,
                  models: elegidos,
                  setting: lugar,
                })
                .then(setResultado)
                .catch((error: unknown) =>
                  setResultado({
                    prompt: error instanceof Error ? error.message : String(error),
                    resultados: [],
                  }),
                )
                .finally(() => setCorriendo(false));
            }}
            className="rounded-xl bg-[var(--color-accent)] px-4 py-2 text-sm font-medium text-white transition hover:opacity-90 disabled:opacity-50"
          >
            {t(corriendo ? 'lab.generando' : 'lab.compararBoton')}
          </button>
        </div>

        {resultado && (
          <div className="mt-4 space-y-3">
            <pre className="overflow-x-auto rounded-xl border border-[var(--color-border-subtle)] bg-[var(--color-surface-sunken)] p-3 text-xs whitespace-pre-wrap">
              {resultado.prompt}
            </pre>

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {resultado.resultados.map((r) => (
                <div key={r.model} className="space-y-1">
                  <p className="font-mono text-xs">{r.model}</p>
                  {r.url ? (
                    <a href={r.url} target="_blank" rel="noreferrer noopener">
                      {/*
                        A tamaño completo, y es deliberado: este laboratorio existe para
                        comparar la calidad de dos modelos mirando. Una miniatura borra
                        justamente lo que se está juzgando —el detalle de la piel, el ruido
                        del sensor, los artefactos—, así que acá el peso se paga a propósito.
                      */}
                      <img
                        src={r.url}
                        alt={r.model}
                        className="w-full rounded-xl border border-[var(--color-border-subtle)]"
                      />
                    </a>
                  ) : (
                    <p className="rounded-xl border border-[var(--color-danger)]/40 p-2 text-xs text-[var(--color-danger)]">
                      {r.error}
                    </p>
                  )}
                  <p className="text-xs text-[var(--color-ink-muted)] tabular-nums">
                    {r.medidas ?? '—'} · {(r.ms / 1000).toFixed(1)} s · USD {r.usd.toFixed(3)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
